// Main-thread facade over the semantic-compression worker (B4). Mirrors the
// shape of embeddings.ts — warmCompressor() is a fire-and-forget kickoff that
// main.ts only calls when B4 is effectively enabled, isCompressorReady() lets a
// stage decide without blocking, and the actual work is lazy.
//
// The one hard difference from embedText(): compress() NEVER throws. Every
// failure mode — no model, worker refused to start, timed out, aborted — comes
// back as a `skipped_*` status carrying the *original text unchanged*. B4's
// fail-open behavior is built entirely on that promise, so keep it.

import { contentHash } from './content-hash';
import { COMPRESSOR_MODEL_VERSION } from './compressor-model';
import { estimateTokens } from '../pipeline/token-estimate';
import { getCachedCompression, saveCachedCompression } from '../db/compressor-cache-repo';
import type { CompressorWorkerMessage, CompressorWorkerRequest } from './compressor-worker';

export interface CompressRequest {
  text: string;
  /** Fraction of tokens to keep, e.g. 0.6 = keep ~60%. */
  rate: number;
  /** Force-keep offsets into `text` — see protected-ranges.ts. */
  protectedRanges?: Array<{ start: number; end: number }>;
  signal: AbortSignal;
}

export interface CompressResult {
  text: string;
  tokensBefore: number;
  tokensAfter: number;
  status: 'ok' | 'skipped_model_unavailable' | 'skipped_timeout' | 'skipped_too_short' | 'skipped_aborted';
}

/**
 * Below this there is nothing worth paying a model round trip for, whatever the
 * caller's own minSpanTokens is set to — a floor, not the policy. B4's
 * `minSpanTokens` param is the real knob and defaults well above this.
 */
const MIN_COMPRESSIBLE_TOKENS = 20;

/** Per-span wall-clock cap. Sits under B4's own stage budget so a stuck worker costs one span, not the stage. */
const COMPRESS_TIMEOUT_MS = 800;

let worker: Worker | null = null;
/** Set once the worker has been tried and could not be created (no Worker support, bundling failure, jsdom). */
let workerUnavailable = false;
let modelAvailable = false;
/** Set once the worker has positively told us there is no model, so later spans skip the round trip. */
let modelKnownUnavailable = false;

let nextRequestId = 1;
const pending = new Map<number, (result: CompressResult) => void>();

/**
 * Kicks off worker startup (and with it the model load) in the background.
 * Called from main.ts only when B4 is on, mirroring warmEmbedder(). Cheap and
 * idempotent; safe to call on every settings change.
 */
export function warmCompressor(): void {
  ensureWorker();
}

export function isCompressorReady(): boolean {
  return modelAvailable;
}

function ensureWorker(): Worker | null {
  if (worker || workerUnavailable) return worker;
  try {
    worker = new Worker(new URL('./compressor-worker.ts', import.meta.url), { type: 'module' });
    worker.addEventListener('message', onWorkerMessage);
    worker.addEventListener('error', () => {
      // The worker died mid-flight; release everyone waiting on it rather than
      // letting them ride out their individual timeouts.
      teardown('skipped_model_unavailable');
    });
  } catch {
    // No Worker in this environment (jsdom under vitest, ancient browser).
    // Fail open permanently rather than retrying on every span.
    worker = null;
    workerUnavailable = true;
    modelKnownUnavailable = true;
  }
  return worker;
}

function onWorkerMessage(event: MessageEvent): void {
  const message = event.data as CompressorWorkerMessage;
  if ('type' in message) {
    modelAvailable = message.available;
    modelKnownUnavailable = !message.available;
    return;
  }
  const resolve = pending.get(message.requestId);
  if (!resolve) return; // already timed out or aborted
  pending.delete(message.requestId);
  resolve(message.result);
}

function teardown(status: CompressResult['status']): void {
  const waiting = Array.from(pending.values());
  pending.clear();
  worker = null;
  workerUnavailable = true;
  modelAvailable = false;
  modelKnownUnavailable = true;
  // These resolvers each close over their own original text, so they know what
  // to hand back; the status is all we can contribute from here.
  for (const resolve of waiting) resolve({ text: '', tokensBefore: 0, tokensAfter: 0, status });
}

/** Never throws — every path resolves with a status and, unless `ok`, the untouched input text. */
export async function compress(req: CompressRequest): Promise<CompressResult> {
  const tokensBefore = estimateTokens(req.text);
  const skip = (status: CompressResult['status']): CompressResult => ({
    text: req.text,
    tokensBefore,
    tokensAfter: tokensBefore,
    status,
  });

  if (req.signal.aborted) return skip('skipped_aborted');
  if (tokensBefore < MIN_COMPRESSIBLE_TOKENS) return skip('skipped_too_short');

  const key = await cacheKey(req.text, req.rate);
  if (key) {
    try {
      const cached = await getCachedCompression(key);
      if (cached) {
        return { text: cached.text, tokensBefore: cached.tokensBefore, tokensAfter: cached.tokensAfter, status: 'ok' };
      }
    } catch {
      // Cache is an optimization; a broken IndexedDB must not fail the request.
    }
  }

  if (modelKnownUnavailable) return skip('skipped_model_unavailable');

  const active = ensureWorker();
  if (!active) return skip('skipped_model_unavailable');

  const result = await roundTrip(active, req, skip);
  if (result.status !== 'ok' || !key) return result;

  try {
    await saveCachedCompression({
      hash: key,
      text: result.text,
      tokensBefore: result.tokensBefore,
      tokensAfter: result.tokensAfter,
    });
  } catch {
    // Same as above — a write failure just means we recompute next time.
  }
  return result;
}

/** Posts one request and races the reply against the abort signal and the per-span timeout. */
function roundTrip(
  active: Worker,
  req: CompressRequest,
  skip: (status: CompressResult['status']) => CompressResult,
): Promise<CompressResult> {
  const requestId = nextRequestId++;

  return new Promise<CompressResult>((resolve) => {
    let settled = false;
    const finish = (result: CompressResult) => {
      if (settled) return;
      settled = true;
      pending.delete(requestId);
      clearTimeout(timer);
      req.signal.removeEventListener('abort', onAbort);
      // A resolver reached through teardown() has no text of its own to give
      // back, so restore the caller's input here rather than leaking ''.
      resolve(result.status === 'ok' ? result : skip(result.status));
    };
    const onAbort = () => finish(skip('skipped_aborted'));
    const timer = setTimeout(() => finish(skip('skipped_timeout')), COMPRESS_TIMEOUT_MS);

    pending.set(requestId, finish);
    req.signal.addEventListener('abort', onAbort, { once: true });

    const message: CompressorWorkerRequest = {
      requestId,
      text: req.text,
      rate: req.rate,
      protectedRanges: req.protectedRanges ?? [],
    };
    try {
      active.postMessage(message);
    } catch {
      finish(skip('skipped_model_unavailable'));
    }
  });
}

async function cacheKey(text: string, rate: number): Promise<string | null> {
  try {
    return await contentHash(`${COMPRESSOR_MODEL_VERSION} ${rate} ${text}`);
  } catch {
    return null; // no SubtleCrypto (insecure origin) — just skip memoization
  }
}
